import React from "react";
import * as m from "../../lib/model";
import { qsbsBump } from "./Qsbs";
import { Card } from "../shared";
import { Row } from "./RoomTabs";
import { ShieldCheck, ArrowRight } from "lucide-react";

const pct = (n) => (isNaN(n) ? "n/a" : m.fmtPct(n));

export function AskReturnsBlock({ M, ask, minCommit, classes = [], timeline = [], showInvest, onInvest }) {
  // Same waterfall as the Dashboard / Returns page: LP class equity incl. rolled legacy, GP co-invest netted.
  const wf = m.lpWaterfall(M), su = m.sourcesUses(M), hold = m.effectiveHold(M);
  const bump = qsbsBump(M);
  const cls = classes.filter((c) => +c.min > 0);
  const effMin = cls.length ? Math.min(...cls.map((c) => +c.min)) : minCommit;
  const close = timeline.find((t) => /clos/i.test(t.label || "") && t.date);
  const equityPct = su.totalUses ? (ask / su.totalUses) * 100 : 0;
  const yr1 = wf.rows[0] ? wf.rows[0].lpYear / wf.lpEquity : 0;
  const interim = wf.rows.slice(0, -1).reduce((a, r) => a + r.lpYear, 0);
  return (
    <div className="ir-2col eq ir-gap" data-testid="ir-ask-returns">
      <Card title="The ask" testid="ir-the-ask" right={showInvest && <button className="btn btn-sm btn-share" onClick={onInvest} data-testid="ir-ask-commit">Soft-commit <ArrowRight size={11} /></button>}>
        <div className="ir-bigstat"><span>LP equity raise</span><b data-testid="ir-ask-amount">{m.fmt$(ask)}</b><em>{equityPct > 0 ? `${equityPct.toFixed(0)}% of ${m.fmt$(su.totalUses)} total uses` : "sponsor / investor equity"}</em></div>
        <Row l="Minimum commitment" v={m.fmt$(effMin)} />
        {cls.length > 0 && <Row l="Share classes" v={cls.map((c) => c.name).filter(Boolean).join(" · ") || `${cls.length} tiers`} />}
        <Row l="Preferred return" v={`${M.prefReturn}% cumulative`} />
        <Row l="LP / GP split after pref" v={`${M.lpSplit == null ? 80 : M.lpSplit} / ${100 - (M.lpSplit == null ? 80 : M.lpSplit)}`} />
        <Row l="Senior debt alongside" v={`${m.fmt$(m.loanAmt(M))} @ ${M.rate}%`} />
        <Row l="Hold period" v={M.holdIndefinite ? "Indefinite" : `${M.hold} years`} />
        <Row l="Target close" v={close ? close.date : "TBD"} />
      </Card>
      <Card title="The returns" testid="ir-the-returns">
        <div className="ir-ret-grid">
          <div><span>LP MOIC</span><b data-testid="ir-ret-moic">{m.fmtX(wf.lpMoic)}</b>
            {bump && <i className="ir-bump" data-testid="ir-ret-bump">+{m.fmtX(bump.x)} QSBS after tax{bump.unlevered ? " · unlevered" : ""}</i>}</div>
          <div><span>LP IRR</span><b data-testid="ir-ret-irr">{pct(wf.lpIrr)}</b>
            {bump && bump.bps > 0 && <i className="ir-bump" data-testid="ir-ret-irr-bump">+{bump.bps} bps QSBS after tax</i>}</div>
          <div><span>Per $100k</span><b>{m.fmt$(100000 * wf.lpMoic)}</b></div>
        </div>
        <Row l="Total LP distributions" v={m.fmt$(wf.lpTotal)} />
        <Row l="· Preferred return paid" v={m.fmt$(wf.prefTotal)} />
        <Row l="· Return of capital" v={m.fmt$(wf.capTotal)} />
        <Row l="· Profit split to LPs" v={m.fmt$(Math.max(0, wf.lpTotal - wf.prefTotal - wf.capTotal))} />
        <Row l="Year-1 cash yield" v={pct(yr1 * 100)} />
        <Row l={`Cash before exit (Yrs 1–${Math.max(1, hold - 1)})`} v={m.fmt$(interim)} />
        <Row l="Target MOIC" v={m.fmtX(M.moicTarget || 2)} />
        <div className="ir-foot-note" style={{ marginTop: 8 }}>Base case, before taxes and fund expenses. Illustrative only.</div>
      </Card>
    </div>
  );
}

export function LpAdvantage({ M, text, qsbs }) {
  const body = text || `LPs sit senior to the sponsor: a ${M.prefReturn}% cumulative preferred return accrues before the GP participates, capital is returned in full before any profit split, and the GP is paid only on performance (${100 - (M.lpSplit == null ? 80 : M.lpSplit)}% of profits after pref and return of capital${M.mgmtFeePct > 0 ? `, ${M.mgmtFeePct}% management fee` : ", no management fee"}). ${m.coverage(M).dscr >= 1.25 ? `Year-${m.coverage(M).year} debt service is covered ${m.fmtX(m.coverage(M).dscr)}, so distributions do not depend on growth.` : "Downside is protected by hard-asset collateral and conservative leverage."}${qsbs ? " Stock is structured to qualify for QSBS (§1202) treatment, so gains may be federally tax-free after a five-year hold." : ""}`;
  return (
    <div className="ir-callout" data-testid="ir-lp-advantage">
      <span className="ir-callout-h"><ShieldCheck size={12} /> The LP advantage</span>
      {body}
    </div>
  );
}
